import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadClaimEventStatuses,
    loadParticipantStatusReasonDetails
} from '../../store/directusService';
import { updateClaimEvent } from '../../store/participantService';
import { getObjFromId } from '../../utils/directusFunctions';
import { hasRole, SUPERUSER } from '../../utils/userRoles';
import Button from '../formElements/Button';
import RadioButtons from '../formElements/RadioButtons';
import SingleSelect from '../formElements/SingleSelect';
import ScreenError from '../ui/messageBanners/ScreenError';

import ClaimStatusField from './ClaimStatusField';
import { validate } from './validateClaim';

import form from '../../commonStyles/formStyles.module.css';

const _UPDATE_MESSAGE = 'Claim event has been updated';

const Claim = ({ eligibilityGroupId, claimType, claimEvent, roles, onClose }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialErrorState = { hasStartStatus: { error: false, message: '' } };
    const initialState = {
        id: '',
        eligibilityGroupId: '',
        claimTypeId: '',
        claimTypeStatuses: [],
        closeParticipantOnClaimPaid: false,
        participantClosureStatusReasonId: ''
    };
    const acceptedRoles = [SUPERUSER];
    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState(initialErrorState);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const claimEventStatuses = useSelector(
        (state) => state.entities.directusService.claimEventStatuses
    );
    const participantStatusReasons = useSelector(
        (state) => state.entities.directusService.participantStatusReasonDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (participantStatusReasons?.length < 1) dispatch(loadParticipantStatusReasonDetails());
    }, []);

    useEffect(() => {
        claimEventStatuses?.length < 1 && dispatch(loadClaimEventStatuses());
        setNewEntry((prev) => ({ ...prev, eligibilityGroupId, claimTypeId: claimType.id }));
    }, [claimType.id, eligibilityGroupId]);

    useEffect(() => {
        if (
            claimEvent.claimTypeId !== claimType.id ||
            claimEvent.eligibilityGroupId !== eligibilityGroupId ||
            claimEventStatuses?.length < 1
        )
            return;

        if (Object.keys(claimEvent?.claimTypeStatuses).length < 1) {
            const initialStatuses = claimEventStatuses.map((el) => ({
                statusId: el.id,
                startingStatus: false,
                claimTypeToStatusIds: []
            }));
            setNewEntry((prev) => ({
                ...prev,
                ...claimEvent,
                claimTypeStatuses: initialStatuses
            }));
        } else setNewEntry((prev) => ({ ...prev, ...claimEvent }));
    }, [claimEvent, claimEventStatuses]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _UPDATE_MESSAGE) onExit();
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onCloseParticipantOnClaimPaidUpdate = (value) => {
        if (!value) {
            setNewEntry((prev) => ({
                ...prev,
                closeParticipantOnClaimPaid: value,
                participantClosureStatusReasonId: ''
            }));
            clearError('participantClosureStatusReasonId');
        } else setNewEntry((prev) => ({ ...prev, closeParticipantOnClaimPaid: value }));
    };

    const onUpdateFromStatus = (statusId, checked) => {
        checked
            ? setNewEntry((prev) => ({
                  ...prev,
                  claimTypeStatuses: [
                      ...newEntry.claimTypeStatuses,
                      { statusId, startingStatus: false, claimTypeToStatusIds: [] }
                  ]
              }))
            : setNewEntry((prev) => ({
                  ...prev,
                  claimTypeStatuses: newEntry.claimTypeStatuses
                      .filter((el) => el.statusId !== statusId)
                      .map((el) => ({
                          ...el,
                          claimTypeToStatusIds: el.claimTypeToStatusIds.filter(
                              (entry) => entry !== statusId
                          )
                      }))
              }));
    };

    const onUpdateStarting = (statusId, checked) => {
        const updated = newEntry.claimTypeStatuses.map((el) => ({
            ...el,
            startingStatus: el.statusId === statusId ? checked : el.startingStatus
        }));
        clearError('hasStartStatus');
        setNewEntry((prev) => ({ ...prev, claimTypeStatuses: updated }));
    };

    const onUpdateToStatuses = (statusId, ids) => {
        const claimTypeStatuses = newEntry.claimTypeStatuses.map((el) =>
            el.statusId === statusId ? { ...el, claimTypeToStatusIds: ids } : el
        );
        setNewEntry((prev) => ({ ...prev, claimTypeStatuses }));
    };

    const onExit = () => {
        setNewEntry(initialState);
        setErrors(initialErrorState);
        onClose();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate(newEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        if (newEntry.id) dispatch(updateClaimEvent(newEntry, _UPDATE_MESSAGE));
    };

    // RENDER
    return (
        <div>
            <form className={form.formWrapper} onSubmit={onSubmit}>
                <div className={form.formSection}>
                    <RadioButtons
                        id="closeParticipantOnClaimPaid"
                        label="Close Participant On Claim At Paid Status"
                        value={newEntry.closeParticipantOnClaimPaid ?? false}
                        onChange={(option) => onCloseParticipantOnClaimPaidUpdate(option)}
                    />
                    {newEntry.closeParticipantOnClaimPaid && (
                        <SingleSelect
                            id="participantClosureStatusReasonId"
                            label="Closure Participant Status Reason"
                            placeholder="Select a closure participant status reason..."
                            disabled={!newEntry.closeParticipantOnClaimPaid}
                            mandatory={!!newEntry.closeParticipantOnClaimPaid}
                            menuItems={participantStatusReasons}
                            selectedId={newEntry.participantClosureStatusReasonId}
                            selected={getObjFromId(
                                participantStatusReasons,
                                newEntry.participantClosureStatusReasonId
                            )}
                            error={errors.participantClosureStatusReasonId}
                            onChange={(chosenId) =>
                                onUpdate('participantClosureStatusReasonId', chosenId)
                            }
                        />
                    )}
                </div>
                {claimEventStatuses.map((el) => (
                    <ClaimStatusField
                        key={el.id}
                        claimTypeId={claimType.id}
                        status={el}
                        roles={roles}
                        newEntry={newEntry}
                        existingClaim={claimEvent}
                        onUpdateToStatuses={onUpdateToStatuses}
                        onUpdateFromStatus={onUpdateFromStatus}
                        onUpdateStarting={onUpdateStarting}
                    />
                ))}

                <Button
                    id="eligibilityGroupClaimUpdateButton"
                    disabled={!hasRole(acceptedRoles, roles) || isButtonDisabled}
                    content="Update Claim Type"
                    error={errors.button}
                    clearError={() => clearError('button')}
                />

                {errors?.hasStartStatus?.error && (
                    <ScreenError
                        error={errors.hasStartStatus}
                        clearError={clearError}
                        allowDismiss={false}
                        name="hasStartStatus"
                    />
                )}
            </form>
        </div>
    );
};

Claim.propTypes = {
    eligibilityGroupId: PropTypes.string,
    claimType: PropTypes.object,
    claimEvent: PropTypes.object,
    roles: PropTypes.arrayOf(PropTypes.string),
    onClose: PropTypes.func
};

export default Claim;
